// Copyright (c) 2024 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.

import React, { useState, useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { WalletContext, isWalletContextLoaded } from 'wallet/context';
import {
    TrashcanIcon,
    EditIcon,
    AddContactIcon,
} from 'components/Common/CustomIcons';
import Modal from 'components/Common/Modal';
import { ModalInput, ModalTextArea } from 'components/Common/Inputs';
import CashtabSwitch from 'components/Common/Switch';
import { toast } from 'react-toastify';
import PrimaryButton, {
    SecondaryButton,
    IconButton,
    CopyIconButton,
} from 'components/Common/Buttons';
import {
    WalletsList,
    WalletsPanel,
    Wallet,
    WalletRow,
    WalletLeftColumn,
    WalletName,
    SvgButtonPanel,
    ActivateButton,
    AddressShareModal,
    WalletAddressRow,
    WalletInfo,
    WalletNameText,
    WalletAddress,
    CopyButton,
    ActiveIndicator,
    HdBadge,
    HdSwitchRow,
    HdSwitchLabel,
} from 'components/Wallets/styles';
import { getWalletNameError, validateMnemonic } from 'validation';
import {
    createCashtabWallet,
    generateMnemonic,
    StoredCashtabWallet,
    isStoredHdWallet,
} from 'wallet';
import { previewAddress } from 'helpers';
import { sortWalletsForDisplay } from 'wallet';
import { Event } from 'components/Common/GoogleAnalytics';
import {
    SettingsPageHeaderLink,
    BackIcon,
    StyledConfigure,
} from 'components/Configure/Configure';
import { buildConnectCallbackUrl, isValidHttpsReturnUrl } from 'deeplinks';
import { openExternalUrl } from 'helpers/openExternalUrl';

interface WalletsFormData {
    renamedWalletName: string;
    walletToBeDeletedName: string;
    newWalletName: string;
    mnemonic: string;
}

interface WalletsFormDataErrors {
    renamedWalletName: false | string;
    walletToBeDeletedName: false | string;
    newWalletName: false | string;
    mnemonic: false | string;
}

const Wallets = () => {
    const ContextValue = useContext(WalletContext);
    if (!isWalletContextLoaded(ContextValue)) {
        // Confirm we have all context required to load the page
        return null;
    }
    const {
        cashtabState,
        setLoading,
        updateCashtabState,
        getWalletByAddress,
        saveWallet,
        deleteWallet: deleteStoredWallet,
    } = ContextValue;
    const { wallets, contactList } = cashtabState;
    const { ecashWallet } = ContextValue;
    if (!ecashWallet) {
        return null;
    }
    const activeStoredWallet = getWalletByAddress(ecashWallet.address);
    if (!activeStoredWallet) {
        return null;
    }

    const emptyFormData: WalletsFormData = {
        renamedWalletName: '',
        walletToBeDeletedName: '',
        newWalletName: '',
        mnemonic: '',
    };

    const emptyFormDataErrors: WalletsFormDataErrors = {
        renamedWalletName: false,
        walletToBeDeletedName: false,
        newWalletName: false,
        mnemonic: false,
    };

    // State variables
    const [formData, setFormData] = useState<WalletsFormData>(emptyFormData);
    const [formDataErrors, setFormDataErrors] =
        useState<WalletsFormDataErrors>(emptyFormDataErrors);
    const [walletToBeRenamed, setWalletToBeRenamed] =
        useState<null | StoredCashtabWallet>(null);
    const [walletToBeDeleted, setWalletToBeDeleted] =
        useState<null | StoredCashtabWallet>(null);
    const [showNewWalletModal, setShowNewWalletModal] =
        useState<boolean>(false);
    // When true, New/Import confirm creates an HD wallet; otherwise single-address.
    // Defaults off; toggled by the HD checkbox in each modal.
    const [createWalletAsHd, setCreateWalletAsHd] = useState<boolean>(false);
    const [showImportWalletModal, setShowImportWalletModal] =
        useState<boolean>(false);
    const [showAddressShareModal, setShowAddressShareModal] =
        useState<boolean>(false);
    const [isConnectRequest, setIsConnectRequest] = useState<boolean>(false);
    const [connectReturnUrl, setConnectReturnUrl] = useState<string | null>(
        null,
    );
    const [connectReturnToBrowser, setConnectReturnToBrowser] =
        useState<boolean>(false);
    const [connectRequestOrigin, setConnectRequestOrigin] = useState<
        string | null
    >(null);
    const [searchParams] = useSearchParams();
    // Guard double-clicks / overlapping activates. Do not debounce in render:
    // lodash.debounce created per render leaks timers across unmount and can
    // activate a wallet after a later delete (flaky cashtab-tests).
    const activateWalletInFlight = useRef(false);

    // Check for address sharing URL parameter on component mount
    useEffect(() => {
        const connect = searchParams.get('connect');
        const returnUrl = searchParams.get('return_url');
        if (connect === '1' && returnUrl && isValidHttpsReturnUrl(returnUrl)) {
            setIsConnectRequest(true);
            setConnectReturnUrl(returnUrl);
            setConnectReturnToBrowser(
                searchParams.get('returnToBrowser') === '1',
            );
            setConnectRequestOrigin(new URL(returnUrl).hostname);
            setShowAddressShareModal(true);
            return;
        }

        if (
            !window.location ||
            !window.location.hash ||
            window.location.hash === '#/wallets'
        ) {
            return;
        }

        try {
            const windowHash = window.location.hash;
            const queryStringArray = windowHash.split('#/wallets?');
            if (queryStringArray.length < 2) {
                return;
            }
            const queryString = queryStringArray[1];
            const queryStringParams = new URLSearchParams(queryString);
            const shareAddresses = queryStringParams.get('shareAddresses');

            if (shareAddresses === 'true') {
                setShowAddressShareModal(true);
            }
        } catch {
            // If you can't parse this, forget about it
            return;
        }
    }, [searchParams]);

    const closeAddressShareModal = () => {
        setShowAddressShareModal(false);
        setIsConnectRequest(false);
        setConnectReturnUrl(null);
        setConnectReturnToBrowser(false);
        setConnectRequestOrigin(null);
    };

    const handleRejectedConnect = async () => {
        closeAddressShareModal();
        if (connectReturnToBrowser && Capacitor.isNativePlatform()) {
            await CapacitorApp.exitApp();
        }
    };

    /**
     * Update formData with user input
     * e.target.value will be input value
     * e.target.name will be name of originating input field
     */
    const handleInputCommon = (name: string, value: string) => {
        if (name === 'renamedWalletName' || name === 'newWalletName') {
            setFormDataErrors(previous => ({
                ...previous,
                [name]: getWalletNameError(value, wallets),
            }));
        }
        if (name === 'walletToBeDeletedName') {
            // We are handling input for user confirmation of walletToBeDeleted
            // We only expect this if walletToBeDeleted has been set
            if (walletToBeDeleted === null) {
                return;
            }
            const walletToBeDeletedNameError =
                value === 'delete ' + walletToBeDeleted.name
                    ? false
                    : `Input must exactly match "delete ${walletToBeDeleted.name}"`;
            setFormDataErrors(previous => ({
                ...previous,
                [name]: walletToBeDeletedNameError,
            }));
        }
        if (name === 'mnemonic') {
            setFormDataErrors(previous => ({
                ...previous,
                [name]:
                    validateMnemonic(value) === true
                        ? false
                        : 'Invalid 12-word mnemonic. Note: all letters must be lowercase.',
            }));
        }
        setFormData(previous => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        handleInputCommon(name, value);
    };

    const handleTextAreaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        handleInputCommon(name, value);
    };

    /**
     * Get user input to rename a wallet
     * We only expect to be in this function if walletToBeRenamed is not null
     */
    const renameWallet = async () => {
        if (walletToBeRenamed === null) {
            // We can only rename a walletToBeRenamed
            return;
        }

        // Find the wallet you want to rename
        const walletToUpdate = wallets.find(
            wallet => wallet.mnemonic === walletToBeRenamed.mnemonic,
        );

        if (typeof walletToUpdate === 'undefined') {
            // We always expect to find it, since only wallets that exist are rendered to the user
            return;
        }

        const oldName = walletToUpdate.name;
        const renamedWallet = {
            ...walletToUpdate,
            name: formData.renamedWalletName,
        };

        try {
            await saveWallet(renamedWallet);
            toast.success(
                `“${oldName}” renamed to “${formData.renamedWalletName}”`,
            );
        } catch (error) {
            console.error('Error renaming wallet', error);
            toast.error(`Unable to rename wallet ${walletToBeRenamed.name}`);
        }
        // Clear walletToBeRenamed field to hide the modal
        setWalletToBeRenamed(null);

        // Clear wallet rename input
        setFormData(previous => ({
            ...previous,
            renamedWalletName: '',
        }));
    };

    /**
     * Delete wallet on successful user confirmation
     */
    const deleteWallet = async () => {
        // We only call this function if we have a walletToBeDeleted
        if (walletToBeDeleted === null) {
            return;
        }

        try {
            await deleteStoredWallet(walletToBeDeleted.address);
            toast.success(`“${walletToBeDeleted.name}” deleted`);
        } catch (error) {
            console.error('Error deleting wallet', error);
            toast.error(`Unable to delete wallet ${walletToBeDeleted.name}`);
        }

        // Reset walletToBeDeleted to hide the modal
        setWalletToBeDeleted(null);

        // Clear wallet to delete input
        setFormData(previous => ({
            ...previous,
            walletToBeDeletedName: '',
        }));
    };

    const clearNewWalletNameForm = () => {
        setFormData(previous => ({
            ...previous,
            newWalletName: '',
        }));
        setFormDataErrors(previous => ({
            ...previous,
            newWalletName: false,
        }));
    };

    const openNewWalletModal = () => {
        setCreateWalletAsHd(false);
        setShowImportWalletModal(false);
        clearNewWalletNameForm();
        setFormData(previous => ({
            ...previous,
            mnemonic: '',
        }));
        setFormDataErrors(previous => ({
            ...previous,
            mnemonic: false,
        }));
        setShowNewWalletModal(true);
    };

    const closeNewWalletModal = () => {
        setShowNewWalletModal(false);
        setCreateWalletAsHd(false);
        clearNewWalletNameForm();
    };

    const openImportWalletModal = () => {
        setShowNewWalletModal(false);
        setCreateWalletAsHd(false);
        clearNewWalletNameForm();
        setFormData(previous => ({
            ...previous,
            mnemonic: '',
        }));
        setFormDataErrors(previous => ({
            ...previous,
            mnemonic: false,
        }));
        setShowImportWalletModal(true);
    };

    const closeImportWalletModal = () => {
        setShowImportWalletModal(false);
        setCreateWalletAsHd(false);
        clearNewWalletNameForm();
        setFormData(previous => ({
            ...previous,
            mnemonic: '',
        }));
        setFormDataErrors(previous => ({
            ...previous,
            mnemonic: false,
        }));
    };

    /**
     * Generate a new wallet with the user-chosen name and add it to wallets.
     * Uses createWalletAsHd when the HD checkbox is checked.
     */
    const addNewWallet = async () => {
        const walletName = formData.newWalletName;
        if (walletName === '' || formDataErrors.newWalletName !== false) {
            return;
        }

        // Generate a new wallet with a new mnemonic
        const mnemonic = generateMnemonic();
        const newAddedWallet = createCashtabWallet(
            mnemonic,
            walletName,
            createWalletAsHd ? { hd: true } : undefined,
        );

        // Technically possible though almost impossibly improbable for a wallet with
        // the same mnemonic to already exist. Cover the edge case.
        const walletAlreadyInWalletsSomehow = wallets.find(
            wallet => wallet.mnemonic === newAddedWallet.mnemonic,
        );
        if (typeof walletAlreadyInWalletsSomehow !== 'undefined') {
            toast.error(
                `By a vanishingly small chance, this wallet already existed in saved wallets. Please try again.`,
            );
            // Do not add this wallet
            return;
        }

        // Event("Category", "Action", "Label")
        // Track number of times a different wallet is activated
        Event(
            'Configure.js',
            'Create Wallet',
            createWalletAsHd ? 'New HD' : 'New',
        );
        try {
            await saveWallet(newAddedWallet);
            toast.success(
                createWalletAsHd
                    ? `New HD wallet “${newAddedWallet.name}” added to wallets`
                    : `New wallet “${newAddedWallet.name}” added to wallets`,
            );
            closeNewWalletModal();
        } catch (error) {
            console.error('Error adding wallet', error);
            toast.error('Failed to add wallet. Please try again.');
        }
    };

    /**
     * Add a new imported wallet to cashtabState wallets object.
     * Uses createWalletAsHd when the HD checkbox is checked.
     */
    async function importNewWallet() {
        const walletName = formData.newWalletName;
        if (
            walletName === '' ||
            formDataErrors.newWalletName !== false ||
            formData.mnemonic === '' ||
            formDataErrors.mnemonic !== false
        ) {
            return;
        }

        // Make sure no existing wallets have this mnemonic
        const walletInWallets = wallets.find(
            wallet => wallet.mnemonic === formData.mnemonic,
        );

        if (typeof walletInWallets !== 'undefined') {
            // Import error modal
            console.error(
                `Cannot import: wallet already exists (name: “${walletInWallets.name}”)`,
            );
            toast.error(
                `Cannot import: wallet already exists (name: “${walletInWallets.name}”)`,
            );
            // Do not clear form data in this case
            return;
        }

        // Create a new wallet from mnemonic with the user-chosen name
        const newImportedWallet = createCashtabWallet(
            formData.mnemonic,
            walletName,
            createWalletAsHd ? { hd: true } : undefined,
        );

        // Event("Category", "Action", "Label")
        // Track number of times a different wallet is activated
        Event(
            'Configure.js',
            'Create Wallet',
            createWalletAsHd ? 'Imported HD' : 'Imported',
        );

        try {
            await saveWallet(newImportedWallet);
            toast.success(
                createWalletAsHd
                    ? `New imported HD wallet “${newImportedWallet.name}” added to your saved wallets`
                    : `New imported wallet “${newImportedWallet.name}” added to your saved wallets`,
            );
            closeImportWalletModal();
        } catch (error) {
            console.error('Error importing wallet', error);
            toast.error('Failed to import wallet. Please try again.');
        }
    }

    /**
     * Add a wallet to contacts
     */
    const addWalletToContacts = async (wallet: StoredCashtabWallet) => {
        const addressToAdd = wallet.address;

        // Check to see if the contact exists
        const contactExists = contactList.find(
            contact => contact.address === addressToAdd,
        );

        if (typeof contactExists !== 'undefined') {
            // Contact exists
            // Not expected to ever happen from Tx.js as user should not see option to
            // add an existing contact
            toast.error(`${addressToAdd} already exists in Contacts`);
        } else {
            contactList.push({
                name: wallet.name,
                address: addressToAdd,
            });
            // update localforage and state
            await updateCashtabState({ contactList: contactList });
            toast.success(
                `${wallet.name} (${addressToAdd}) added to Contact List`,
            );
        }
    };

    /**
     * Share wallet address with a connected dApp or copy to clipboard (web flow).
     */
    const shareWalletAddress = async (address: string, walletName: string) => {
        const copiedWallet = wallets.find(wallet => wallet.name === walletName);

        if (
            copiedWallet &&
            activeStoredWallet &&
            activeStoredWallet.mnemonic !== copiedWallet.mnemonic
        ) {
            Event('Configure.js', 'Activate', '');
            await updateCashtabState({
                activeWalletAddress: copiedWallet.address,
            });
        }

        if (isConnectRequest && connectReturnUrl) {
            const callbackUrl = buildConnectCallbackUrl(
                connectReturnUrl,
                address,
            );
            closeAddressShareModal();
            await openExternalUrl(callbackUrl);
            if (connectReturnToBrowser && Capacitor.isNativePlatform()) {
                await CapacitorApp.exitApp();
            }
            return;
        }

        if (navigator.clipboard) {
            await navigator.clipboard.writeText(address);
        }

        // Close the tab after copying - this works when the tab was opened by JavaScript
        window.close();
    };

    const activateWallet = async (walletToActivate: StoredCashtabWallet) => {
        if (activateWalletInFlight.current) {
            return;
        }
        if (walletToActivate.address === activeStoredWallet.address) {
            return;
        }
        activateWalletInFlight.current = true;
        setLoading(true);
        // Event("Category", "Action", "Label")
        // Track number of times a different wallet is activated
        Event('Configure.js', 'Activate', '');

        try {
            // Update active wallet address in state (which also persists to storage)
            // This triggers the useEffect that calls initializeWallet()
            await updateCashtabState({
                activeWalletAddress: walletToActivate.address,
            });
        } catch (error) {
            console.error('Error activating wallet:', error);
            toast.error('Failed to activate wallet. Please try again.');
        } finally {
            activateWalletInFlight.current = false;
            setLoading(false);
        }
    };

    return (
        <StyledConfigure>
            <SettingsPageHeaderLink
                to="/configure"
                aria-label="Back to settings"
            >
                <BackIcon aria-hidden="true" />
                Wallets
            </SettingsPageHeaderLink>
            {walletToBeRenamed !== null && (
                <Modal
                    title={`Rename “${walletToBeRenamed.name}”?`}
                    handleOk={renameWallet}
                    handleCancel={() => setWalletToBeRenamed(null)}
                    showCancelButton
                    disabled={
                        formDataErrors.renamedWalletName !== false ||
                        formData.renamedWalletName === ''
                    }
                >
                    <ModalInput
                        placeholder="Enter new wallet name"
                        name="renamedWalletName"
                        value={formData.renamedWalletName}
                        error={formDataErrors.renamedWalletName}
                        handleInput={handleInput}
                    />
                </Modal>
            )}
            {walletToBeDeleted !== null && (
                <Modal
                    title={`Delete “${walletToBeDeleted.name}”?`}
                    handleOk={deleteWallet}
                    handleCancel={() => setWalletToBeDeleted(null)}
                    showCancelButton
                    disabled={
                        formDataErrors.walletToBeDeletedName !== false ||
                        formData.walletToBeDeletedName === ''
                    }
                >
                    <ModalInput
                        placeholder={`Type "delete ${walletToBeDeleted.name}" to confirm`}
                        name="walletToBeDeletedName"
                        value={formData.walletToBeDeletedName}
                        handleInput={handleInput}
                        error={formDataErrors.walletToBeDeletedName}
                    />
                </Modal>
            )}
            {showNewWalletModal && (
                <Modal
                    title="New wallet"
                    handleOk={addNewWallet}
                    handleCancel={closeNewWalletModal}
                    showCancelButton
                    disabled={
                        formDataErrors.newWalletName !== false ||
                        formData.newWalletName === ''
                    }
                >
                    <ModalInput
                        placeholder="Enter a name for this wallet"
                        name="newWalletName"
                        value={formData.newWalletName}
                        error={formDataErrors.newWalletName}
                        handleInput={handleInput}
                    />
                    <HdSwitchRow>
                        <CashtabSwitch
                            name="createWalletAsHd"
                            checked={createWalletAsHd}
                            handleToggle={() =>
                                setCreateWalletAsHd(prev => !prev)
                            }
                        />
                        <HdSwitchLabel>HD</HdSwitchLabel>
                    </HdSwitchRow>
                </Modal>
            )}
            {showImportWalletModal && (
                <Modal
                    title={`Import wallet`}
                    handleOk={importNewWallet}
                    handleCancel={closeImportWalletModal}
                    showCancelButton
                    disabled={
                        formDataErrors.mnemonic !== false ||
                        formData.mnemonic === '' ||
                        formDataErrors.newWalletName !== false ||
                        formData.newWalletName === ''
                    }
                >
                    <ModalInput
                        placeholder="Enter a name for this wallet"
                        name="newWalletName"
                        value={formData.newWalletName}
                        error={formDataErrors.newWalletName}
                        handleInput={handleInput}
                    />
                    <ModalTextArea
                        placeholder="mnemonic (seed phrase)"
                        name="mnemonic"
                        value={formData.mnemonic}
                        error={formDataErrors.mnemonic}
                        handleInput={handleTextAreaInput}
                        height={96}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                    />
                    <HdSwitchRow>
                        <CashtabSwitch
                            name="importWalletAsHd"
                            checked={createWalletAsHd}
                            handleToggle={() =>
                                setCreateWalletAsHd(prev => !prev)
                            }
                        />
                        <HdSwitchLabel>HD</HdSwitchLabel>
                    </HdSwitchRow>
                </Modal>
            )}
            {showAddressShareModal && (
                <Modal
                    title="Connect Wallet"
                    description={
                        connectRequestOrigin
                            ? `Wallet connect request from ${connectRequestOrigin}`
                            : 'Select a wallet to connect'
                    }
                    handleCancel={closeAddressShareModal}
                    showCancelButton={false}
                    showButtons={false}
                >
                    <AddressShareModal>
                        <div
                            style={{
                                marginBottom: '16px',
                                textAlign: 'center',
                            }}
                        >
                            <button
                                onClick={() =>
                                    isConnectRequest
                                        ? handleRejectedConnect()
                                        : closeAddressShareModal()
                                }
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                }}
                            >
                                Reject
                            </button>
                        </div>
                        {sortWalletsForDisplay(activeStoredWallet, wallets).map(
                            (wallet, index) => (
                                <WalletAddressRow
                                    key={`${wallet.name}_${index}`}
                                >
                                    <WalletInfo>
                                        <WalletNameText>
                                            {wallet.name}
                                            {index === 0 && (
                                                <ActiveIndicator>
                                                    [active]
                                                </ActiveIndicator>
                                            )}
                                        </WalletNameText>
                                        <WalletAddress>
                                            {(() => {
                                                const preview = previewAddress(
                                                    wallet.address,
                                                );
                                                const firstChar =
                                                    preview.charAt(0);
                                                const rest = preview.slice(1);
                                                return (
                                                    <>
                                                        <span
                                                            style={{
                                                                color: 'var(--accent)',
                                                            }}
                                                        >
                                                            {firstChar}
                                                        </span>
                                                        {rest}
                                                    </>
                                                );
                                            })()}
                                        </WalletAddress>
                                    </WalletInfo>
                                    <CopyButton
                                        onClick={() =>
                                            shareWalletAddress(
                                                wallet.address,
                                                wallet.name,
                                            )
                                        }
                                    >
                                        Connect
                                    </CopyButton>
                                </WalletAddressRow>
                            ),
                        )}
                    </AddressShareModal>
                </Modal>
            )}
            <WalletsList title="Wallets">
                <WalletsPanel>
                    {sortWalletsForDisplay(activeStoredWallet, wallets).map(
                        (wallet, index) => {
                            const isActive = index === 0;
                            return (
                                <Wallet key={`${wallet.name}_${index}`}>
                                    <WalletLeftColumn>
                                        <WalletName className="notranslate">
                                            {wallet.name}
                                            {isStoredHdWallet(wallet) && (
                                                <HdBadge title="Hierarchical deterministic wallet">
                                                    HD
                                                </HdBadge>
                                            )}
                                        </WalletName>
                                        <ActivateButton
                                            $active={isActive}
                                            aria-label={
                                                isActive
                                                    ? `${wallet.name} is active`
                                                    : `Activate ${wallet.name}`
                                            }
                                            onClick={
                                                isActive
                                                    ? undefined
                                                    : () =>
                                                          activateWallet(wallet)
                                            }
                                            disabled={isActive}
                                        >
                                            {isActive ? 'Active' : 'Activate'}
                                        </ActivateButton>
                                    </WalletLeftColumn>
                                    <SvgButtonPanel>
                                        <IconButton
                                            name={`Delete ${wallet.name}`}
                                            icon={<TrashcanIcon />}
                                            onClick={() =>
                                                setWalletToBeDeleted(wallet)
                                            }
                                        />
                                        <IconButton
                                            name={`Add ${wallet.name} to contacts`}
                                            icon={<AddContactIcon />}
                                            onClick={() =>
                                                addWalletToContacts(wallet)
                                            }
                                        />
                                        <IconButton
                                            name={`Rename ${wallet.name}`}
                                            icon={<EditIcon />}
                                            onClick={() =>
                                                setWalletToBeRenamed(wallet)
                                            }
                                        />

                                        <CopyIconButton
                                            name={`Copy address of ${wallet.name}`}
                                            data={wallet.address}
                                        />
                                    </SvgButtonPanel>
                                </Wallet>
                            );
                        },
                    )}
                </WalletsPanel>
                <WalletRow>
                    <PrimaryButton onClick={openNewWalletModal}>
                        New Wallet
                    </PrimaryButton>
                </WalletRow>
                <WalletRow>
                    <SecondaryButton onClick={openImportWalletModal}>
                        Import Wallet
                    </SecondaryButton>
                </WalletRow>
            </WalletsList>
        </StyledConfigure>
    );
};

export default Wallets;
